import { useRef } from 'react';
import ReactDOM from 'react-dom';
import Fab from '@mui/material/Fab';
import { Cancel } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PolygonIcon from '../../../icons/PolygonIcon';
import { emitCustomEvent } from 'react-custom-events';
import Events from '../../../defaults/Events';
import './ValuesToolbar.css';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ValuesToolbarProps {
    /** Called when the user cancels the add-value workflow */
    onCancel: () => void;
    existing: boolean;
    editing: boolean;
    /** Called when geometry drawing is complete */
    onDrawComplete?: (geometry: any) => void;
    onDrawCancel?: () => void;
    /** Called when confirming geometry edit */
    onConfirm?: () => void;
    /** Called when copy from fire perimeter is initiated */
    onCopyFromFirePerimeter?: () => void;
    /** Called when a shapefile is selected for upload */
    onShapefileSelected?: (file: File) => void;
    /** Whether this is a postfire action (enables fire perimeter copy) */
    isPostFire?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────
//
// Portals action Fabs (+ a Cancel Fab) to the map toolbar anchor element,
// mirroring the CreatePerimeterToolbar / CreateActualsToolbar pattern.

export default function ValuesToolbar({ onCancel, onDrawComplete, existing, editing, onConfirm, onCopyFromFirePerimeter, onShapefileSelected, isPostFire }: ValuesToolbarProps) {
    const portalTarget = document.getElementById('editPerimeterAnchorEl');
    const inputReaderRef = useRef<HTMLInputElement>(null);
    if (!portalTarget) return null;

    const handleDrawPerimeter = () => {
        // Enable draw mode for polygon geometry (values are always polygons)
        emitCustomEvent(Events.EnableDrawMode, {
            geometry: null,
            callbackParams: {
                isPostFireValue: true,
            },
            callback: (geometry: any) => {
                // When drawing completes, notify parent component
                if (onDrawComplete) {
                    onDrawComplete(geometry);
                }
            },
        });
    };

    const handleConfirm = () => {
        // Emit CompleteDrawMode to finalize the geometry editing
        emitCustomEvent(Events.CompleteDrawMode);

        // Call the confirm callback
        if (onConfirm) {
            onConfirm();
        }
    };

    const handleUploadClick = () => {
        inputReaderRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        if (file && onShapefileSelected) {
            onShapefileSelected(file);
        }
        // Reset so the same file can be re-selected
        if (inputReaderRef.current) {
            inputReaderRef.current.value = '';
        }
    };

    return ReactDOM.createPortal(
        <div className='edit-geometry-container edit-geometry-selector' id='ValuesToolbarAnchorEl'>
            {!existing && (
                <Fab
                    variant='extended'
                    size='medium'
                    color='primary'
                    aria-label='Draw Perimeter'
                    onClick={handleDrawPerimeter}
                >
                    <PolygonIcon sx={{ mr: 1 }} />
                    Draw
                </Fab>
            )}
            {isPostFire && onCopyFromFirePerimeter && (
                <Fab
                    variant='extended'
                    size='medium'
                    color='primary'
                    aria-label='Copy Fire Perimeter'
                    onClick={onCopyFromFirePerimeter}
                    sx={{ fontSize: '0.8rem', lineHeight: 1.1 }}
                >
                    <ContentCopyIcon sx={{ mr: 0.5 }} />
                    Copy Perimeter
                </Fab>
            )}
            {onShapefileSelected && (
                <Fab
                    variant='extended'
                    size='medium'
                    color='primary'
                    aria-label='Upload Shapefile'
                    onClick={handleUploadClick}
                >
                    <UploadFileIcon sx={{ mr: 1 }} />
                    Upload
                </Fab>
            )}
            {editing && (
                <Fab
                    variant='extended'
                    size='medium'
                    color='primary'
                    aria-label='Save'
                    onClick={handleConfirm}
                >
                    <PolygonIcon sx={{ mr: 1 }} />
                    Save
                </Fab>
            )}
            {/* Cancel */}
            <Fab
                variant='extended'
                size='medium'
                color='primary'
                aria-label='Cancel'
                onClick={onCancel}
            >
                <Cancel sx={{ mr: 1 }} />
                Cancel
            </Fab>
            <input
                type='file'
                ref={inputReaderRef}
                accept='.zip'
                multiple={false}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </div>,
        portalTarget
    );
}

